import { ProductCard } from '@components/product'
import cn from 'clsx'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import Slider from 'react-slick'
import type { SlimProduct } from '@lib/similar-products/types'
import { deriveMetalColour, getCategory } from '@lib/similar-products/cascade'

// Reuse the cascade module's category resolver so the UI and backend agree
// on which product types get a Similar Items panel.
function isSupportedType(productType?: string): boolean {
  return getCategory(productType) != null
}

function isWatchType(productType?: string): boolean {
  return getCategory(productType) === 'watches'
}

interface Props {
  /** Pre-calculated handles from the custom.similar_products metafield */
  similarHandles?: string[]
  /** Fallback props for the live cascade API when metafield is empty */
  handle: string
  productType?: string
  gender?: string[]
  brand?: string
  material?: string
  gemstone?: string
  style?: string
  price?: number
  tags?: string[]
}

const sliderSettings = (length: number) => ({
  className: cn({ 'hide-slider': length < 5 }),
  dots: true,
  infinite: length > 5,
  speed: 500,
  slidesToShow: 5,
  slidesToScroll: 5,
  arrows: true,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        className: cn({ 'hide-slider': length < 4 }),
        slidesToShow: 4,
        slidesToScroll: 4,
        infinite: length > 4,
        dots: true,
        arrows: true,
      },
    },
    {
      breakpoint: 768,
      settings: {
        className: cn({ 'hide-slider': length < 3 }),
        slidesToShow: 3,
        slidesToScroll: 3,
        infinite: length > 3,
        dots: true,
        arrows: false,
      },
    },
    {
      breakpoint: 425,
      settings: {
        className: cn({ 'hide-slider': length < 2 }),
        slidesToShow: 2,
        slidesToScroll: 2,
        infinite: length > 2,
        dots: true,
        arrows: false,
      },
    },
  ],
})

function deriveNamedTags(tags: string[]): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {}
  for (const tag of tags) {
    const idx = tag.indexOf(':')
    if (idx <= 0) continue
    const key = tag.slice(0, idx)
    const value = tag.slice(idx + 1)
    const existing = out[key]
    if (existing === undefined) out[key] = value
    else if (Array.isArray(existing)) existing.push(value)
    else out[key] = [existing, value]
  }
  return out
}

const SkeletonCards: FC = () => (
  <div className="flex gap-4">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex-1 min-w-0">
        <div className="bg-ui-lightest-grey animate-pulse aspect-[4/5] rounded" />
        <div className="mt-3 h-3 bg-ui-lightest-grey animate-pulse rounded w-16" />
        <div className="mt-2 h-3 bg-ui-lightest-grey animate-pulse rounded w-32" />
        <div className="mt-2 h-3 bg-ui-lightest-grey animate-pulse rounded w-16" />
      </div>
    ))}
  </div>
)

/**
 * Fetch products by pre-calculated handles from the by-handles API.
 * Falls back to the cascade API if handles are empty or unavailable.
 */
async function fetchByHandles(handles: string[]): Promise<SlimProduct[]> {
  const res = await fetch('/api/similar-products/by-handles', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handles }),
  })
  if (!res.ok) throw new Error(`by-handles API ${res.status}`)
  return res.json()
}

async function fetchCascadeFallback(props: Props): Promise<SlimProduct[]> {
  const {
    handle,
    productType,
    gender,
    brand,
    material,
    gemstone,
    style,
    price,
    tags,
  } = props

  if (!productType) return []

  const features = (tags || [])
    .filter((t) => t.toLowerCase().startsWith('feature:'))
    .map((t) => t.slice('feature:'.length))

  // Derive metal colour from material first, then `metal_colour:` tags.
  // Same logic the cron and APIs use, so the cascade fallback's source
  // product is scored consistently with the rest of the pipeline.
  const derivedMetalColour = deriveMetalColour(material, tags)

  const params = new URLSearchParams({
    handle,
    type: productType,
    ...(gender?.length ? { gender: gender.join(',') } : {}),
    ...(brand ? { brand } : {}),
    ...(material ? { material } : {}),
    ...(gemstone ? { gemstone } : {}),
    ...(style ? { style } : {}),
    ...(features.length ? { features: features.join(',') } : {}),
    ...(price != null ? { price: String(price) } : {}),
    ...(derivedMetalColour ? { metalColour: derivedMetalColour } : {}),
  })

  const res = await fetch(`/api/similar-products?${params}`)
  if (!res.ok) throw new Error(`cascade API ${res.status}`)
  return res.json()
}

const SimilarItems: FC<Props> = (props) => {
  const {
    handle,
    productType,
    similarHandles,
    gender,
    brand,
    material,
    gemstone,
    style,
    price,
    tags,
  } = props
  const supported = isSupportedType(productType)
  // Stringify array deps so the callback identity is stable across renders
  // when the contents are unchanged (parent often passes new array refs).
  const similarHandlesKey = similarHandles?.join(',') ?? ''
  const genderKey = gender?.join(',') ?? ''
  const tagsKey = tags?.join(',') ?? ''
  const [products, setProducts] = useState<SlimProduct[] | null>(null)
  const [loading, setLoading] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const fetchedRef = useRef(false)

  // Reset when navigating to a different product
  useEffect(() => {
    fetchedRef.current = false
    setProducts(null)
    setLoading(false)
  }, [handle])

  const fetchSimilar = useCallback(async () => {
    if (fetchedRef.current || !supported) return
    fetchedRef.current = true
    setLoading(true)

    try {
      let results: SlimProduct[] = []

      const cascadeInput: Props = {
        handle,
        productType,
        gender,
        brand,
        material,
        gemstone,
        style,
        price,
        tags,
      }

      if (similarHandles?.length) {
        // Primary path: fetch live data for pre-calculated handles
        results = await fetchByHandles(similarHandles)

        // If too many were unavailable, backfill with cascade
        if (results.length < 5) {
          const fallback = await fetchCascadeFallback(cascadeInput)
          const existingHandles = new Set(results.map((p) => p.handle))
          for (const p of fallback) {
            if (!existingHandles.has(p.handle)) {
              results.push(p)
              existingHandles.add(p.handle)
            }
            if (results.length >= 8) break
          }
        }
      } else {
        // Fallback: no metafield yet (new product, cron hasn't run)
        results = await fetchCascadeFallback(cascadeInput)
      }

      // Filter out sold-out watches. The cascade spec doesn't pre-filter
      // watches by stock (so the same pool is used for relationship matching
      // regardless of inventory), but the storefront should not surface
      // sold-out watches in the carousel — the user asked for this explicitly.
      if (isWatchType(productType)) {
        results = results.filter((p) => p.inStock)
      }

      setProducts(results.length > 0 ? results : null)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[SimilarItems] fetch failed:', err)
      setProducts(null)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handle,
    productType,
    supported,
    similarHandlesKey,
    genderKey,
    tagsKey,
    brand,
    material,
    gemstone,
    style,
    price,
  ])

  useEffect(() => {
    if (!supported) return

    const el = sentinelRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fetchSimilar()
          observer.disconnect()
        }
      },
      { rootMargin: '400px' }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [productType, fetchSimilar])

  // Return null immediately for unsupported types (giftware, clocks, etc.)
  // to avoid rendering skeleton + making an unnecessary API call
  if (!supported) return null
  if (!loading && products === null && fetchedRef.current) return null

  return (
    <div
      ref={sentinelRef}
      className="wrapper mt-12 mb-10 laptop:mb-16 product-slick"
    >
      <div className="uppercase text-ui-darkest-grey font-normal text-20 leading-6 laptop:text-24 laptop:leading-8">
        You Might Also Like
      </div>
      <div className="mt-8">
        {loading || !products ? (
          <SkeletonCards />
        ) : (
          <Slider {...sliderSettings(products.length)}>
            {products.map((product) => {
              const namedTags = deriveNamedTags(product.tags || [])
              const brandTag =
                (typeof namedTags?.Brand === 'string'
                  ? namedTags.Brand
                  : Array.isArray(namedTags?.Brand)
                  ? namedTags.Brand[0]
                  : undefined) ??
                product.namedMetafields?.brand ??
                product.vendor

              return (
                <ProductCard
                  key={product.handle}
                  id={product.id}
                  name={product.title}
                  handle={product.handle}
                  price={product.price}
                  compareAtPrice={undefined}
                  images={product.image ? [{ url: product.image.url }] : []}
                  link={`/${product.handle}`}
                  brand={brandTag?.replace(/_/g, ' ') ?? ''}
                  namedTags={namedTags}
                  tags={product.tags || []}
                  isSoldOut={!product.inStock}
                />
              )
            })}
          </Slider>
        )}
      </div>
    </div>
  )
}

export default SimilarItems
